import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { getTagList, type TagListItem } from "@api/services/facebook";

export interface TagsState {
  tags: TagListItem[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: TagsState = {
  tags: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchTags = createAsyncThunk(
  "tags/fetchTags",
  async (
    params: { offset?: number; limit?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const { offset = 0, limit = 100 } = params;
      const fetchedTags = await getTagList({ offset, limit });
      return fetchedTags || [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch tags"
      );
    }
  }
);

const tagsSlice = createSlice({
  name: "tags",
  initialState,
  reducers: {
    clearTags: (state) => {
      state.tags = [];
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTags.fulfilled,
        (state, action: PayloadAction<TagListItem[]>) => {
          state.loading = false;
          state.tags = action.payload || [];
          state.error = null;
          state.lastFetched = Date.now();
        }
      )
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTags } = tagsSlice.actions;
export default tagsSlice.reducer;
